import { NETWORK } from '@shared/utils/constant'
import {
  getBaseRequestKey,
  BaseClientRequestCache
} from '@/app/providers/clientRequestCache'
import type {
  ClientRequestCacheKeyValue,
  BaseClientRequestCacheParams
} from '@/app/providers/clientRequestCache'

export { CLIENT_REQUEST_CACHE_FOCUS_EXPIRY_MS } from '@/app/providers/clientRequestCache'

export const CLIENT_REQUEST_KEYS = {
  accountStats: 'accountStats',
  accountPoints: 'accountPoints',
  currentEpoch: 'currentEpoch',
  marketTrades: 'marketTrades',
  marketHistory: 'marketHistory',
  fundingHistory: 'fundingHistory',
  historicalPnl: 'historicalPnl',
  marketMarkPrice: 'marketMarkPrice',
  accountInvitees: 'accountInvitees',
  historicalBalance: 'historicalBalance',
  positionMarketPrices: 'positionMarketPrices',
  portfolioOverview: 'portfolioOverview',
  inviteeReferrer: 'inviteeReferrer',
  tcDerivativeOrders: 'tcDerivativeOrders',
  referrerEligibility: 'referrerEligibility',
  referralAccountStats: 'referralAccountStats',
  feeDiscountAccountInfo: 'feeDiscountAccountInfo',
  tcDerivativePositions: 'tcDerivativePositions',
  accountPortfolioBalances: 'accountPortfolioBalances',
  tcDerivativeOrdersHistory: 'tcDerivativeOrdersHistory',
  tcDerivativeTradesHistory: 'tcDerivativeTradesHistory'
} as const

export const CLIENT_REQUEST_CACHE_TTL_MS = {
  [CLIENT_REQUEST_KEYS.marketHistory]: 60_000,
  [CLIENT_REQUEST_KEYS.fundingHistory]: 15_000,
  [CLIENT_REQUEST_KEYS.portfolioOverview]: 60_000,
  [CLIENT_REQUEST_KEYS.feeDiscountAccountInfo]: 60_000
}

type ClientRequestKey =
  (typeof CLIENT_REQUEST_KEYS)[keyof typeof CLIENT_REQUEST_KEYS]

type ClientRequestCacheTtlKey = keyof typeof CLIENT_REQUEST_CACHE_TTL_MS

export type ApiCallsRequestCacheParams = Omit<
  BaseClientRequestCacheParams,
  'getTtlMs' | 'baseScope'
>

/**
 * API-call request cache.
 * App request keys and TTL rules live here.
 * Shared cache mechanics live in BaseClientRequestCache.
 */
export class ApiCallsRequestCache extends BaseClientRequestCache {
  constructor(params: ApiCallsRequestCacheParams = {}) {
    super({
      ...params,
      baseScope: [NETWORK],
      getTtlMs: getApiCallsRequestCacheTtlMs
    })
  }
}

/**
 * Builds stable request keys with the active network baked in.
 * Scope values are normalized so object key order and undefined fields do not create duplicate entries.
 */
export function createRequestKey(
  key: ClientRequestKey,
  ...scope: ClientRequestCacheKeyValue[]
) {
  return apiCallsRequestCache.createRequestKey(key, ...scope)
}

function getApiCallsRequestCacheTtlMs(requestKey: string) {
  const key = getBaseRequestKey(requestKey)
  const ttlMs = CLIENT_REQUEST_CACHE_TTL_MS[key as ClientRequestCacheTtlKey]

  if (!ttlMs) {
    throw new Error(`Missing client request cache TTL for ${key}`)
  }

  return ttlMs
}

export const apiCallsRequestCache = new ApiCallsRequestCache()
